import React, { useState } from 'react';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';

import { subtract } from 'utils/calculator';

import Modal from 'components/ui/Modal';
import { CurrencyInput } from 'components/ui/Inputs';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { ActionButton, ConfirmButton } from 'components/ui/Button';

import {
  StyledButtonGroup,
  StyledErrorMessage,
  StyledFormFooter,
  StyledTotalContainer,
  StyledInputContainer,
  StyledInputWrapper,
  StyledLabel,
  StyledWarningMessage
} from '../../ExtrasHotel/ManageHotel/ManageHotel.styles';

const ManageInsurance = ({ passenger, date, policyNumber, currency, price, onCancel, loading }) => {
  const { register, handleSubmit, formState, watch } = useForm();

  const { errors } = formState;

  const [isModalVisible, setIsModalVisible] = useState(false);

  const toggleModal = () => setIsModalVisible(!isModalVisible);

  const hasErrors = Boolean(Object.keys(errors).length > 0);

  const watchFee = watch('fee');

  const calculateTotal = () => {
    if (!watchFee) {
      return 0;
    }

    const currentFee = Number(watchFee);

    return subtract(price, currentFee);
  };

  const handleCancelClick = () => {
    toggleModal();
  };

  const handleOnSubmit = async (formData) => {
    await onCancel({ fee: Number(formData.fee) });

    toggleModal();
  };

  return (
    <>
      <Modal isOpen={isModalVisible} title={<FormattedMessage id="extras.insurance.modal.title" />} onClose={toggleModal}>
        <form onSubmit={handleSubmit(handleOnSubmit)} data-cy="insurance-info-form">
          <StyledInputContainer>
            <StyledInputWrapper>
              <label htmlFor="passengerName">
                <StyledLabel>
                  <FormattedMessage id="extras.passenger" />
                </StyledLabel>
                {passenger}
              </label>
            </StyledInputWrapper>

            <StyledInputWrapper>
              <label htmlFor="date">
                <StyledLabel>
                  <FormattedMessage id="extras.insurance.date" />
                </StyledLabel>
                <FormattedDate value={date} year="numeric" month="short" day="numeric" weekday="long" />
              </label>
            </StyledInputWrapper>

            <StyledInputWrapper>
              <label htmlFor="policyNumber">
                <StyledLabel>
                  <FormattedMessage id="extras.insurance.policyNumber" />
                </StyledLabel>
                {policyNumber}
              </label>
            </StyledInputWrapper>

            <StyledInputWrapper>
              <StyledLabel>
                <FormattedMessage id="extras.insurance.modal.price" />
              </StyledLabel>
              <FormattedCurrency currency={currency} amount={price} />
            </StyledInputWrapper>

            <StyledInputWrapper>
              <StyledLabel>
                <FormattedMessage id="extras.insurance.modal.fee" />
              </StyledLabel>
              <CurrencyInput
                currency={currency}
                dataCy="fee"
                dataTestId="fee"
                isControlled={false}
                {...register('fee', {
                  required: true,
                  min: 0.0,
                  max: price
                })}
                step={0.01}
              />
            </StyledInputWrapper>
          </StyledInputContainer>

          <StyledTotalContainer data-cy="total">
            <FormattedMessage id="extras.insurance.modal.total" />
            :
            <FormattedCurrency currency={currency} amount={calculateTotal()} />
          </StyledTotalContainer>

          <StyledFormFooter>
            <StyledButtonGroup>
              <ConfirmButton type="submit" data-testid="submit" data-cy="submit" disabled={loading} variant="primary">
                <FormattedMessage id="extras.hotel.modal.confirm" />
              </ConfirmButton>

              <ActionButton type="button" data-testid="close" onClick={toggleModal} disabled={loading}>
                <FormattedMessage id="extras.hotel.modal.close" />
              </ActionButton>
            </StyledButtonGroup>

            <StyledWarningMessage>
              <FormattedMessage id="extras.hotel.modal.warning" />
            </StyledWarningMessage>
          </StyledFormFooter>

          {hasErrors && (
            <StyledErrorMessage data-testid="errors">
              <FormattedMessage id="extras.insurance.modal.error" />
            </StyledErrorMessage>
          )}
        </form>
      </Modal>

      <StyledButtonGroup>
        <ActionButton type="button" data-cy="cancel-insurance" onClick={handleCancelClick} disabled={false}>
          <FormattedMessage id="extras.hotel.modal.cancel" />
        </ActionButton>
      </StyledButtonGroup>
    </>
  );
};

ManageInsurance.propTypes = {
  passenger: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  policyNumber: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  price: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default ManageInsurance;
